
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchWithRetry } from '../utils/fetchWithRetry';
import { Server, Mail, Eye, EyeOff } from 'lucide-react';
import { signInWithGoogle } from '../utils/googleAuth';
import { signInWithApple } from '../utils/appleAuth';
import { getGoogleClientId, APPLE_AUTH_CONFIG } from '../config/auth';

// Inline consent line shown above signup and OAuth buttons. The legal-link
// styling is shared so the two call-sites stay visually identical.
function LegalConsent({ lead }) {
  return (
    <p className="mt-3 mb-1 text-center text-xs landscape:text-[0.625rem] text-[var(--color-ink-subtle)] font-serif italic leading-snug">
      {lead}{' '}
      <Link to="/terms" className="underline hover:text-[var(--color-ink-main)] transition-colors">Terms of Service</Link>
      {' '}and{' '}
      <Link to="/privacy" className="underline hover:text-[var(--color-ink-main)] transition-colors">Privacy Policy</Link>.
    </p>
  );
}

// Lazy load Capacitor
let Capacitor = null;
const loadCapacitor = async () => {
  if (Capacitor) return Capacitor;
  try {
    const module = await import('@capacitor/core');
    Capacitor = module.Capacitor;
  } catch (e) {
    Capacitor = { isNativePlatform: () => false };
  }
  return Capacitor;
};

function AuthScreen({ isLogin, setIsLogin, email, setEmail, password, setPassword, handleAuth, error, onGoogleLogin, onNativeGoogleLogin, onAppleLogin, apiUrl, setApiUrl }) {
  const googleButtonRef = useRef(null);

  // Check if running in Electron
  const isElectron = typeof window !== 'undefined' && window.electronAPI;

  // Check if on Capacitor platform (initially assume not, will update)
  const [isCapacitor, setIsCapacitor] = useState(false);
  const [capacitorPlatform, setCapacitorPlatform] = useState('web');

  // Only show backend toggle in development AND not running in Capacitor or Electron
  const [showBackendToggle] = useState((process.env.NODE_ENV === 'development' || !process.env.NODE_ENV) && !isCapacitor && !isElectron);
  const [isGoogleSigningIn, setIsGoogleSigningIn] = useState(false);
  const [googleSignInError, setGoogleSignInError] = useState('');

  // Track when Google script is loaded
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);

  // Apple Sign-In state (single button works across web/iOS/Electron because
  // signInWithApple() normalizes platform differences for us).
  const [isAppleSigningIn, setIsAppleSigningIn] = useState(false);
  const [appleSignInError, setAppleSignInError] = useState('');

  // Check Capacitor platform on mount
  useEffect(() => {
    loadCapacitor().then(cap => {
      setIsCapacitor(cap.isNativePlatform());
      if (cap.getPlatform) {
        setCapacitorPlatform(cap.getPlatform());
      }
    });
  }, []);

  // Wait for Google Sign-In script to load
  useEffect(() => {
    if (isCapacitor || isElectron) {
      // Don't need Google script for native platforms
      return;
    }

    // Check if already loaded
    if (window.google && window.google.accounts && window.google.accounts.id) {
      setIsGoogleLoaded(true);
      return;
    }

    // Poll for Google script to be available
    const checkGoogleLoaded = setInterval(() => {
      if (window.google && window.google.accounts && window.google.accounts.id) {
        setIsGoogleLoaded(true);
        clearInterval(checkGoogleLoaded);
      }
    }, 100);

    // Cleanup interval on unmount or after 10 seconds
    const timeout = setTimeout(() => {
      clearInterval(checkGoogleLoaded);
    }, 10000);

    return () => {
      clearInterval(checkGoogleLoaded);
      clearTimeout(timeout);
    };
  }, [isCapacitor, isElectron]);

  // Password visibility state
  const [showPassword, setShowPassword] = useState(false);

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
  const [forgotPasswordError, setForgotPasswordError] = useState('');
  const [isSendingReset, setIsSendingReset] = useState(false);

  // Handle forgot password request
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotPasswordError('');
    setForgotPasswordMessage('');
    setIsSendingReset(true);

    try {
      const response = await fetchWithRetry(`${apiUrl}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotPasswordEmail })
      });

      const data = await response.json();

      if (response.ok) {
        setForgotPasswordMessage(data.message);
        setForgotPasswordEmail('');
      } else {
        setForgotPasswordError(data.error || 'Failed to send reset email');
      }
    } catch (error) {
      console.error('Forgot password error:', error);
      setForgotPasswordError('Network error. Please try again.');
    } finally {
      setIsSendingReset(false);
    }
  };

  // Handle Apple Sign-In (web / iOS native / Electron). The utility figures
  // out which path to take based on platform; we just pass the result up.
  const handleAppleSignIn = async () => {
    if (!onAppleLogin) return;
    setIsAppleSigningIn(true);
    setAppleSignInError('');
    try {
      const result = await signInWithApple();
      await onAppleLogin(result);
    } catch (err) {
      console.error('Apple Sign-In failed:', err);
      setAppleSignInError(
        err?.message === 'Sign-in cancelled'
          ? ''
          : "Apple Sign-In isn't available currently. Please sign in with your email and password instead."
      );
    } finally {
      setIsAppleSigningIn(false);
    }
  };

  // Handle native Google Sign-In (for Capacitor)
  const handleNativeGoogleSignIn = async () => {
    setIsGoogleSigningIn(true);
    setGoogleSignInError('');
    try {
      const result = await signInWithGoogle();
      console.log('Native Google Sign-In successful:', result);

      // Call the native Google login handler with the idToken
      if (onNativeGoogleLogin) {
        await onNativeGoogleLogin(result);
      }
    } catch (error) {
      console.error('Native Google Sign-In failed:', error);
      setGoogleSignInError("Google Sign-In isn't available currently. Please sign in with your email and password instead.");
    } finally {
      setIsGoogleSigningIn(false);
    }
  };

  // Initialize Google Sign-In button (web only - not for Capacitor or Electron)
  useEffect(() => {
    // Only initialize web Google Sign-In if NOT on Capacitor or Electron
    const clientId = getGoogleClientId();
    if (!isCapacitor && !isElectron && isGoogleLoaded && clientId && googleButtonRef.current && onGoogleLogin) {
      try {
        window.google.accounts.id.initialize({
          client_id: clientId,
          callback: onGoogleLogin
        });

        // Get the actual width of the container to calculate button width
        const containerWidth = googleButtonRef.current.offsetWidth;
        const buttonWidth = Math.min(containerWidth, 400); // Max 400px

        window.google.accounts.id.renderButton(
          googleButtonRef.current,
          {
            theme: 'filled_black',
            size: 'large',
            width: buttonWidth, // Use numeric pixel value
            text: 'continue_with',
            shape: 'rectangular'
          }
        );
      } catch (error) {
        console.error('Error initializing Google Sign-In:', error);
      }
    }
  }, [onGoogleLogin, isElectron, isCapacitor, isGoogleLoaded]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 landscape:p-2 landscape:py-2 relative overflow-hidden landscape:overflow-y-auto bg-[var(--color-paper-bg)] text-[var(--color-ink-main)] font-sans transition-colors duration-500">

      {/* Card Container - Hand-drawn paper style */}
      <div className="relative z-10 w-full max-w-md landscape:max-w-4xl animate-slideUp">
        {/* Paper sheet effect */}
        <div className="bg-[var(--color-paper-warm)] rounded-sm shadow-xl border border-[var(--color-ink-subtle)]/10 p-10 landscape:px-8 landscape:py-3">
          <div className="absolute top-0 left-0 right-0 bottom-0 border-2 border-[var(--color-ink-main)]/5 rounded-sm pointer-events-none" style={{ borderRadius: '15.9375rem 0.9375rem 14.0625rem 0.9375rem / 0.9375rem 14.0625rem 0.9375rem 15.9375rem' }}></div>

          {/* Two-column layout in landscape. Uses flex (not grid) because
              Chrome 53 has no `display: grid`. `landscape:flex` activates the
              row layout in landscape; in portrait both children render as block
              and stack naturally. */}
          <div className="landscape:flex landscape:space-x-12">
            {/* Left column: Logo, Title, Description (landscape only) */}
            <div className="landscape:flex-1 text-center mb-10 landscape:mb-0 landscape:flex landscape:flex-col landscape:justify-center landscape:items-center landscape:border-r landscape:border-[var(--color-ink-subtle)]/10 landscape:pr-8">
              <div className="inline-flex p-4 landscape:p-3 mb-6 landscape:mb-4 border-2 border-[var(--color-ink-main)] rounded-full" style={{ borderRadius: '58% 42% 48% 52% / 53% 45% 55% 47%' }}>
                <img
                  src={process.env.PUBLIC_URL + "/android-chrome-192x192.png"}
                  alt="Steadily.me logo"
                  className="w-16 h-16 landscape:w-20 landscape:h-20 opacity-90 contrast-125 filter sepia-[.25]"
                />
              </div>
              <h1 className="text-5xl landscape:text-4xl font-bold font-serif text-[var(--color-ink-main)] mb-3 landscape:mb-4 tracking-tight">
                Steadily.me
              </h1>
              <p className="text-[var(--color-ink-light)] text-lg landscape:text-base italic font-serif">Your habits, always in sight</p>
            </div>

            {/* Right column: Forms (portrait takes full width) */}
            <div className="landscape:flex-1 landscape:flex landscape:flex-col landscape:justify-center">
              {/* Development Backend Toggle */}
              {showBackendToggle && setApiUrl && (
                <div className="mb-6 landscape:mb-3 p-4 landscape:p-3 bg-yellow-500/5 border border-yellow-500/30 shape-organic">
                  <div className="flex items-center justify-between mb-3 landscape:mb-2">
                    <div className="flex items-center space-x-2">
                      <Server className="w-4 h-4 text-yellow-600" />
                      <span className="text-yellow-700 font-semibold text-sm landscape:text-xs font-serif">DEV MODE</span>
                    </div>
                    <span className="text-xs landscape:text-[0.625rem] text-yellow-700/70 font-serif">Backend Environment</span>
                  </div>

                  <div className="flex space-x-2">
                    <button
                      type="button"
                      onClick={() => setApiUrl('http://localhost:5000')}
                      className={`flex-1 px-3 py-2 landscape:px-2 landscape:py-1.5 font-medium text-sm landscape:text-xs transition-all rounded-lg border ${apiUrl === 'http://localhost:5000'
                        ? 'bg-[var(--color-ink-main)] text-[var(--color-paper-bg)] shadow-md border-transparent'
                        : 'bg-transparent text-[var(--color-ink-main)] hover:bg-[var(--color-ink-main)]/5 border-[var(--color-ink-subtle)]/30'
                        }`}
                    >
                      <div className="font-bold">Local</div>
                      <div className="text-xs landscape:text-[0.625rem] opacity-70">:5000</div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setApiUrl('https://beta.backend.steadily.me')}
                      className={`flex-1 px-3 py-2 landscape:px-2 landscape:py-1.5 font-medium text-sm landscape:text-xs transition-all rounded-lg border ${apiUrl === 'https://beta.backend.steadily.me'
                        ? 'bg-amber-500 text-white shadow-md border-transparent'
                        : 'bg-transparent text-amber-600 hover:bg-amber-500/10 border-amber-500/30'
                        }`}
                    >
                      <div className="font-bold">Beta</div>
                      <div className="text-xs landscape:text-[0.625rem] opacity-70">beta.backend</div>
                    </button>

                    <button
                      type="button"
                      onClick={() => setApiUrl('https://backend.steadily.me')}
                      className={`flex-1 px-3 py-2 landscape:px-2 landscape:py-1.5 font-medium text-sm landscape:text-xs transition-all rounded-lg border ${apiUrl === 'https://backend.steadily.me'
                        ? 'bg-[var(--color-ink-main)] text-[var(--color-paper-bg)] shadow-md border-transparent'
                        : 'bg-transparent text-[var(--color-ink-main)] hover:bg-[var(--color-ink-main)]/5 border-[var(--color-ink-subtle)]/30'
                        }`}
                    >
                      <div className="font-bold">Prod</div>
                      <div className="text-xs landscape:text-[0.625rem] opacity-70">backend</div>
                    </button>
                  </div>

                  <div className="w-full">
                    <input
                      type="text"
                      placeholder="Custom API URL"
                      value={apiUrl}
                      onChange={(e) => setApiUrl(e.target.value)}
                      className="w-full px-3 py-2 landscape:py-1.5 bg-transparent text-[var(--color-ink-main)] text-xs landscape:text-[0.625rem] border-b-2 border-[var(--color-ink-subtle)]/30 focus:border-[var(--color-accent-warm)] focus:outline-none transition-all placeholder-[var(--color-ink-subtle)]/50 font-mono"
                    />
                  </div>
                </div>
              )}

              {showForgotPassword ? (
                // Forgot Password Form
                <>
                  <form onSubmit={handleForgotPassword} className="space-y-5 landscape:space-y-3">
                    <div className="text-center mb-4 landscape:mb-2">
                      <Mail className="w-12 h-12 landscape:w-8 landscape:h-8 mx-auto text-[var(--color-accent-warm)] mb-3 landscape:mb-2" />
                      <h2 className="text-2xl landscape:text-xl font-bold font-serif text-[var(--color-ink-main)] mb-2 landscape:mb-1">Forgot Password?</h2>
                      <p className="text-[var(--color-ink-light)] text-sm landscape:text-xs font-serif">
                        Enter your email and we'll send you a link to reset your password
                      </p>
                    </div>

                    <div className="relative group">
                      <input
                        type="email"
                        placeholder="Email address"
                        value={forgotPasswordEmail}
                        onChange={(e) => setForgotPasswordEmail(e.target.value)}
                        className="w-full px-5 py-4 landscape:px-4 landscape:py-3 bg-transparent text-[var(--color-ink-main)] border-2 border-[var(--color-ink-subtle)]/30 focus:border-[var(--color-ink-main)] focus:outline-none transition-all placeholder-[var(--color-ink-subtle)]/50 font-medium"
                        style={{ borderRadius: '15.9375rem 0.9375rem 14.0625rem 0.9375rem / 0.9375rem 14.0625rem 0.9375rem 15.9375rem' }}
                        required
                      />
                    </div>

                    {forgotPasswordError && (
                      <div className="text-red-600 text-sm landscape:text-xs bg-red-50/50 border-l-4 border-red-500 p-3 landscape:p-2 font-medium">
                        {forgotPasswordError}
                      </div>
                    )}

                    {forgotPasswordMessage && (
                      <div className="text-green-700 text-sm landscape:text-xs bg-green-50/50 border-l-4 border-green-500 p-3 landscape:p-2 font-medium">
                        {forgotPasswordMessage}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={isSendingReset}
                      className="w-full py-4 landscape:py-3 bg-[var(--color-ink-main)] text-[var(--color-paper-bg)] font-bold font-serif text-lg landscape:text-base hover:bg-[var(--color-ink-light)] transition-all transform hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                      style={{ borderRadius: '15.9375rem 0.9375rem 14.0625rem 0.9375rem / 0.9375rem 14.0625rem 0.9375rem 15.9375rem' }}
                    >
                      {isSendingReset ? 'Sending...' : 'Send Reset Link'}
                    </button>
                  </form>

                  <div className="mt-6 landscape:mt-3 text-center">
                    <button
                      onClick={() => {
                        setShowForgotPassword(false);
                        setForgotPasswordEmail('');
                        setForgotPasswordMessage('');
                        setForgotPasswordError('');
                      }}
                      className="text-[var(--color-ink-light)] hover:text-[var(--color-ink-main)] transition-colors text-sm landscape:text-xs font-medium font-serif italic border-b border-transparent hover:border-[var(--color-ink-main)]"
                    >
                      ← Back to sign in
                    </button>
                  </div>
                </>
              ) : (
                // Regular Login/Register Form
                <>
                  <form onSubmit={handleAuth} className="space-y-5 landscape:space-y-2">
                    <div className="relative group">
                      <input
                        type="email"
                        placeholder="Email address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-5 py-4 landscape:px-4 landscape:py-2 bg-transparent text-[var(--color-ink-main)] border-2 border-[var(--color-ink-subtle)]/30 focus:border-[var(--color-ink-main)] focus:outline-none transition-all placeholder-[var(--color-ink-subtle)]/50 font-medium"
                        style={{ borderRadius: '15.9375rem 0.9375rem 14.0625rem 0.9375rem / 0.9375rem 14.0625rem 0.9375rem 15.9375rem' }}
                        required
                      />
                    </div>
                    <div className="relative group">
                      <input
                        type={showPassword ? "text" : "password"}
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full px-5 py-4 pr-12 landscape:px-4 landscape:py-2 landscape:pr-10 bg-transparent text-[var(--color-ink-main)] border-2 border-[var(--color-ink-subtle)]/30 focus:border-[var(--color-ink-main)] focus:outline-none transition-all placeholder-[var(--color-ink-subtle)]/50 font-medium"
                        style={{ borderRadius: '15.9375rem 0.9375rem 14.0625rem 0.9375rem / 0.9375rem 14.0625rem 0.9375rem 15.9375rem' }}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-[var(--color-ink-subtle)] hover:text-[var(--color-ink-main)] transition-colors p-1"
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>

                    {isLogin && (
                      <div className="text-right">
                        <button
                          type="button"
                          onClick={() => setShowForgotPassword(true)}
                          className="text-sm landscape:text-xs text-[var(--color-ink-light)] hover:text-[var(--color-ink-main)] transition-colors font-medium font-serif italic border-b border-transparent hover:border-[var(--color-ink-main)]"
                        >
                          Forgot password?
                        </button>
                      </div>
                    )}

                    {error && (
                      <div className="text-red-600 text-sm landscape:text-xs bg-red-50/50 border-l-4 border-red-500 p-3 landscape:p-2 font-medium">
                        {error}
                      </div>
                    )}

                    <button
                      type="submit"
                      className="w-full py-4 landscape:py-2 bg-[var(--color-ink-main)] text-[var(--color-paper-bg)] font-bold font-serif text-lg landscape:text-base hover:bg-[var(--color-ink-light)] transition-all transform hover:scale-[1.01] active:scale-[0.99] shadow-md"
                      style={{ borderRadius: '15.9375rem 0.9375rem 14.0625rem 0.9375rem / 0.9375rem 14.0625rem 0.9375rem 15.9375rem' }}
                    >
                      {isLogin ? 'Sign In' : 'Create Account'}
                    </button>

                    {!isLogin && <LegalConsent lead="By creating an account you agree to our" />}
                  </form>

                  <div className="mt-6 landscape:mt-1 text-center">
                    <button
                      onClick={() => setIsLogin(!isLogin)}
                      className="text-[var(--color-ink-light)] hover:text-[var(--color-ink-main)] transition-colors text-sm landscape:text-xs font-medium font-serif"
                    >
                      {isLogin ? "Don't have an account? " : 'Already have an account? '}
                      <span className="underline decoration-wavy decoration-[var(--color-accent-warm)] underline-offset-4 font-bold">
                        {isLogin ? "Sign up" : 'Sign in'}
                      </span>
                    </button>
                  </div>
                </>
              )}

              {getGoogleClientId() && !showForgotPassword && (
                <>
                  <div className="my-8 landscape:my-2 flex items-center">
                    <div className="flex-1 h-px bg-[var(--color-ink-subtle)]/20"></div>
                    <span className="px-4 landscape:px-3 text-[var(--color-ink-light)] text-sm landscape:text-xs italic font-serif">or continue with</span>
                    <div className="flex-1 h-px bg-[var(--color-ink-subtle)]/20"></div>
                  </div>

                  {/* Apple Sign-In. App Store Review Guideline 4.8 requires this
                      whenever a third-party social login (Google) is offered.
                      Hidden on Android native (not required and Apple's OAuth
                      flow is awkward inside Android WebView). */}
                  {onAppleLogin && capacitorPlatform !== 'android' && APPLE_AUTH_CONFIG.SERVICES_ID && (
                    <>
                      <button
                        onClick={handleAppleSignIn}
                        disabled={isAppleSigningIn}
                        className="w-full py-4 landscape:py-2 mb-3 landscape:mb-2 bg-black text-white rounded-xl border border-black font-semibold text-lg landscape:text-base hover:bg-neutral-800 transition-all transform hover:scale-[1.01] active:scale-[0.99] flex items-center justify-center space-x-3 landscape:space-x-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                        aria-label="Continue with Apple"
                      >
                        <svg width="20" height="22" viewBox="0 0 20 22" className="landscape:w-4 landscape:h-5" fill="currentColor" aria-hidden="true">
                          <path d="M16.365 11.516c-.026-2.78 2.27-4.114 2.374-4.178-1.292-1.89-3.302-2.149-4.018-2.18-1.71-.173-3.34 1.008-4.207 1.008-.882 0-2.205-.984-3.628-.956-1.866.027-3.589 1.085-4.55 2.755-1.94 3.366-.494 8.34 1.395 11.072.924 1.336 2.024 2.838 3.474 2.785 1.396-.057 1.92-.902 3.606-.902 1.685 0 2.158.902 3.633.872 1.503-.025 2.453-1.36 3.366-2.703 1.062-1.553 1.498-3.058 1.524-3.135-.034-.014-2.927-1.124-2.969-4.438zM13.586 3.494c.762-.927 1.279-2.213 1.137-3.494-1.099.045-2.43.733-3.22 1.658-.708.82-1.328 2.13-1.16 3.39 1.226.094 2.481-.624 3.243-1.554z" />
                        </svg>
                        <span>{isAppleSigningIn ? 'Signing in...' : 'Continue with Apple'}</span>
                      </button>

                      {appleSignInError && (
                        <div className="mb-3 landscape:mb-2 text-red-600 text-sm landscape:text-xs bg-red-50/50 border-l-4 border-red-500 p-3 landscape:p-2 font-medium">
                          {appleSignInError}
                        </div>
                      )}
                    </>
                  )}

                  {(isCapacitor || isElectron) ? (
                    // Native Google Sign-In button for Capacitor (iOS/Android) and Electron
                    <button
                      onClick={handleNativeGoogleSignIn}
                      disabled={isGoogleSigningIn}
                      className="w-full py-4 landscape:py-2 bg-[var(--color-surface-card)] text-[var(--color-ink-main)] rounded-xl border border-[var(--color-ink-subtle)]/20 font-semibold text-lg landscape:text-base hover:bg-[var(--color-surface-input)] transition-all transform hover:scale-[1.01] active:scale-[0.99] flex items-center justify-center space-x-3 landscape:space-x-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                    >
                      <svg width="24" height="24" viewBox="0 0 24 24" className="landscape:w-5 landscape:h-5">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                      </svg>
                      {isGoogleSigningIn ? 'Signing in...' : 'Continue with Google'}
                    </button>
                  ) : (
                    // Web Google Sign-In button (web only)
                    <div ref={googleButtonRef}></div>
                  )}

                  {googleSignInError && (
                    <div className="mt-4 landscape:mt-2 text-red-600 text-sm landscape:text-xs bg-red-50/50 border-l-4 border-red-500 p-3 landscape:p-2 font-medium">
                      {googleSignInError}
                    </div>
                  )}

                  <LegalConsent lead="By continuing with Google or Apple you agree to our" />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AuthScreen;
